// Open House — the new Post a Project's customer wording.
//
// Every string here is approved, verbatim: docs/design/tuw/open-house/
// phase-1-wording.md §1–3 (posting form: writing, sample, editing) and §4
// (after posting), new-flow-wording.md §1–7, and the open-to-every-level
// strings (decision 17). Labels the new flow shares with today's form come from
// today's step configs (lib/post/services/*.js), never from here. A string still awaiting approval would be a visible
// "[wording pending: <key>]" marker; none is pending now.

export const WORDING = {
  // §1 Book details
  titleLabel: "Title or working topic",
  titleHelp: "No title yet? A few words about your topic are enough.",
  titlePlaceholder: "e.g. Gardening in small spaces",
  titleError: "Enter a title or working topic.",
  descriptionLabel: "Short description",
  descriptionHelp:
    "Tell writers what the book is about and who it’s for. Writers see this before deciding whether to request your project.",
  descriptionPlaceholder: "e.g. A practical guide to growing vegetables on a balcony, for beginners with limited space.",
  descriptionError: "Add a short description so writers can understand your book.",

  // §1 Outline choice
  outlineHeading: "Outline",
  outlineIntro: "Add an outline to your project, or supply your own before posting.",
  paidNonfiction: "Add a Professional Outline",
  paidFiction: "Add a Plot Outline",
  // new-flow-wording §28 (approved 2026-10-24): the fee shows only once picked.
  paidHelp: "Add a paid outline. You pay after a writer is assigned.",
  supplied: "Supply my own outline",
  suppliedHelp: "Upload or paste your outline. Writers can read it before requesting your project.",
  choiceError: "Choose an outline service or supply your own outline.",
  supplyLabel: "Your outline",
  modeUpload: "Upload a file",
  modePaste: "Paste outline",
  uploadHelp: "Supported files: .doc, .docx, .pdf or .zip.",
  fileButton: "Choose file",
  uploadEmpty: "No outline uploaded.",
  pasteLabel: "Outline text",
  pastePlaceholder: "Paste your outline here.",
  requiredError: "Upload or paste your outline before posting, or add an outline service.",
  uploading: "Uploading outline…",
  uploaded: "Outline uploaded.",
  uploadFailed: "Your outline didn’t upload. Try again.",
  retryUpload: "Retry upload",
  unsupported: "Upload a .doc, .docx, .pdf or .zip file, or paste your outline.",
  uploadTooLarge: "Upload a file up to 24 MB, or paste your outline.", // new-flow-wording §33
  outlineIncluded: "Outline included in your package.",

  // §4 After posting (writing)
  postedHeading: "Your project is posted",
  postedBody:
    "Writers can now request your project. When you choose a writer, we’ll ask you to finish a short brief with the remaining details.",

  // new-flow-wording §1–4, §7
  reviewHeading: "One last look before you post",
  reviewSub: "Posting is free. Nothing is charged until you choose a freelancer and start the work.",
  terms:
    "I understand that TUW is not responsible for changes if I choose not to review my content or request revisions, or miss the allotted timeframes for either. TUW is also not responsible for additional changes or revisions after completion or fulfillment. Both limits exclude major quality issues or a freelancer’s failure to follow the project’s initial requirements.",
  termsError: "Acknowledge the review and revision terms before posting.",
  postFail: "We couldn’t post your project. Your answers are saved. Try again.",
  postRetry: "Try posting again",
  recommended: "Recommended",

  // new-flow-wording §5–6: bundles
  coverAI: "I allow my cover designer to use AI tools on this cover.",
  bundleError: "Choose a bundle to continue.",

  // Decision 17: open to every writer level (today's All Tier Access),
  // approved by Marco 2026-10-21. The price line takes the formatted range;
  // the per-sample unit is the sample variant.
  openLevels: "Open to every writer level",
  openLevelsDesc: "Let writers of every level request your project. This usually gives you more requests to choose from.",
  openLevelsPrice: (from, to) => `${from} to ${to} per 100 words`,
  openLevelsSamplePrice: (from, to) => `${from} to ${to} per sample`,
  openLevelsTotal: "Your final price depends on the writer whose request you accept.",
  openLevelsPosted: "Each writer’s request will show their price for your project.",

  // phase-1-wording §2 Sample details (the title-or-topic wording is §1's)
  sampleHeading: "Sample details",
  sampleDescriptionHelp:
    "Describe the book and what you want the sample to cover. Writers see this before deciding whether to request your project.",
  sampleDescriptionPlaceholder:
    "e.g. The opening scene of a mystery set in a coastal town, introducing the main character and a missing neighbour.",
  sampleDescriptionError: "Add a short description so writers can understand your sample.",

  // phase-1-wording §3 About your manuscript
  editingDescriptionHelp:
    "Describe your book, its main themes and who it’s for. Editors see this before deciding whether to request your project.",
  editingDescriptionPlaceholder:
    "e.g. A memoir about starting over after retirement, written for readers planning their own next chapter.",
  editingDescriptionError: "Add a short description so editors can understand your book.",
  manuscriptLabel: "Manuscript",
  manuscriptHelp: "Upload your manuscript before posting so editors can review it. Supported files: .doc or .docx.",
  manuscriptButton: "Upload manuscript",
  manuscriptMissing: "Upload your manuscript before posting.",
  manuscriptUnsupported: "Upload your manuscript as a .doc or .docx file.",
  manuscriptUploading: "Uploading manuscript…",
  manuscriptUploaded: "Manuscript uploaded.",
  manuscriptFailed: "Your manuscript didn’t upload. Try again.",

  // §4 After posting (editing)
  postedEditingBody:
    "Editors can now request your project. When you choose an editor, we’ll ask you to review your remaining editing preferences.",
}

// The arrival page around the guest's start choices, approved verbatim:
// new-flow-wording.md §34 (lines from the live site, Astra's A, B and C).
// The stats are the live site's; the rating is production's customer ratings
// of freelancers (31,156, average 4.91, checked 2026-10-27).
export const ARRIVAL = {
  headline: "Post free and choose your writer before you pay.",
  sub: "Every book-writing package includes human copy editing and proofreading.",
  facts: ["Posting is free", "Exact price before you post. Writers don't bid on it.", "Pay once a writer is attached to your project"],
  writingFrom: (rate) => `From ${rate} per 100 words`,
  stats: [
    ["17,500+", "Full-length books written"],
    ["75,000+", "Orders delivered"],
    ["20,000+", "Satisfied customers"],
    ["4.9/5", "Customer ratings of freelancers"],
  ],
  stepsH: "How to Hire Your Writer",
  steps: [
    ["Post Your Project", "Share your book details for free so writers can apply."],
    ["Choose Your Writer", "Review profiles, read samples and chat. Invite a writer or ask our team for help."],
    ["Pay Your Invoice", "Pay once a writer is attached to your project."],
    ["Follow the Writing", "Read and comment in the live document, use project chat and track your order."],
  ],
  checksH: "Written by People, With Checks Included",
  checks: [
    ["Vetted Freelancers", "Every freelancer passes our application, testing and training process."],
    ["Originality Report", "Every manuscript comes with an originality report for you to review before editing."],
    ["You Keep All Copyrights", "You own all delivered content and keep all copyrights."],
  ],
  reviewsH: "Backed By the Best",
  ratingLine: "4.9/5 from 31,000+ customer ratings of freelancers",
  reviews: [
    "By far and away the #1 ghostwriting company for nonfiction self publishers. Consistent delivery time, good communication throughout and the only company I count on to produce books which generate organic 5 star reviews from Amazon customers.",
    "If you're looking for high quality writing en masse, this place does an incredible job. They are super responsive and work with you to create a manuscript that is high quality for whatever you may need.",
    "I have worked with Dibbly | The Urban Writers on a number of projects and each time I have been overwhelmingly satisfied with the quality and timeliness of the work delivered. I look forward to working with them on future projects.",
  ],
  reviewer: "TUW customer",
}

// The guest front door (Front porch), approved verbatim: guest-wording.md §1
// (draft.*, bookmark.*; bookmark.tooManySends adopted on advice), §2 (price.*)
// and §3 (registration.signIn), and
// new-flow-wording.md §21 (startH) and §23 (somethingElse.*, writeBook.*,
// editBook.*). Amounts and currency come from the server's quote.
export const GUEST = {
  startH: "Start a project",
  writeBook: "Book writing",
  writeBookDesc: "Work with a writer on your book, choose a bundle or start with a paid 1,000-word sample.",
  editBook: "Book editing",
  editBookDesc: "Work with an editor on a manuscript you already have.",
  somethingElse: "Something else",
  somethingElseDesc: "Open the current posting form to choose another service.",
  signIn: "Already have an account? Sign in",

  saving: "Saving your draft…",
  saved: "Draft saved",
  restored: "Your draft is ready to continue.",
  newerVersion: "This draft has newer changes from another tab or device. Load the latest version before continuing.",
  loadLatest: "Load latest draft",
  saveFailed: "We couldn’t save your latest changes. Keep this page open and try again.",
  saveRetry: "Try saving again",
  draftExpiredHeading: "This draft has expired",
  draftExpiredBody: (days) => `This draft expired after ${days} days without activity. Start a new project to continue.`,
  startNew: "Start a new project",
  accessUnavailable: "We couldn’t open this draft. Use the private save link from your email.",

  // §1 Save by email (bookmark.*): the offer, its form and states, and the
  // pages a save link can land on. The Privacy Policy line is split round its link.
  bookmarkHeading: "Save your project for later",
  bookmarkBody: "We’ll email you a private link so you can return to this draft on any device.",
  emailLabel: "Email address",
  emailHelp: "Saving your draft doesn’t create an account or post your project.",
  emailRequired: "Enter your email address.",
  emailInvalid: "Enter a valid email address.",
  reminderConsent: "Send me one reminder the next day if I post this project but haven’t confirmed my email.",
  reminderConsentHelp: "Optional. You can save your draft and post without agreeing to a reminder.",
  bookmarkSubmit: "Email my save link",
  bookmarkSkip: "Continue without emailing a link",
  bookmarkSending: "Sending your save link…",
  bookmarkRetention: (days) => `Your draft expires after ${days} days without activity.`,
  privacy: ["How we use your information: ", "Privacy Policy", "."],
  bookmarkSavedHeading: "Your draft is saved",
  bookmarkSavedBody: (email) => `We’ve sent a private save link to ${email}. Your project hasn’t been posted.`,
  bookmarkContinue: "Continue with my project",
  deliveryFailed: "Your draft is saved, but we couldn’t send the email. Check the address and try again.",
  deliveryRetry: "Try sending again",
  changeEmail: "Change email address",
  bookmarkThrottled: "We can’t send the link yet. Your draft is saved here. Try again in a little while.",
  linkExpiredHeading: "This save link has expired",
  linkExpiredBody: "This link can no longer open your draft.",
  linkInvalidHeading: "This save link isn’t valid",
  linkInvalidBody: "Open the full save link from the email we sent you.",
  linkRecovery: "If your draft is still open on another device, continue there. Otherwise, you can start a new project.",

  priceHeading: "Your project price",
  amount: (amount, currency) => `${amount} ${currency}`,
  totalLabel: "Service total",
  taxNote: "Plus applicable tax, calculated at checkout using your billing information.",
  paymentTiming: "Posting is free. Payment comes after a writer is assigned, and work starts once you’ve paid.",
  fixed: "This is the service price for your selected options and writer level. Writers don’t bid on the price.",
  loading: "Calculating your project price…",
  unavailableHeading: "We couldn’t calculate your price",
  unavailableBody: "Try again before posting. You’ll need a current price to review and accept.",
  unavailableRetry: "Try calculating again",
  review: "Review project options",
  combination: "We can’t offer this combination. Review your project options to continue.",
  changedHeading: "Your project price has changed",
  changedBody: "We’ve checked current rates for your saved draft. Review the new service price before posting.",
  changedPrevious: (amount, currency) => `Previously: ${amount} ${currency}`,
  changedCurrent: (amount, currency) => `Now: ${amount} ${currency}, plus applicable tax`,
  changedAccept: "Accept the new service price",
  changedRequired: "Accept the current service price before posting.",
  changedAccepted: "New service price accepted. We’ll lock it when you post.",
  openTax: "The prices shown exclude applicable tax.",
  openChangedHeading: "Your project’s price range has changed",
  openChangedPrevious: (min, max, currency) => `Previously: ${min} to ${max} ${currency}`,
  openChangedCurrent: (min, max, currency) => `Now: ${min} to ${max} ${currency}, plus applicable tax`,
  openChangedAccept: "Accept the updated price range",
}

// The account screen (Reservation), approved verbatim: guest-wording.md §3
// (registration.*). The agreement is split round its eight links into text and
// [policy key, link text] parts. A name over the server's limit uses
// registration.firstName/lastName.tooLong (adopted on advice 2026-10-23).
export const REGISTRATION = {
  heading: "Create your account to post",
  body: "We’ll save your project while you confirm your email. Writers can see it after confirmation.",
  firstNameLabel: "First name",
  lastNameLabel: "Last name",
  nameHelp: "Use your name for your account. You’ll add the book’s author name or pen name in the brief.",
  emailLabel: "Email address",
  emailHelp: "We’ll send your confirmation code and link here.",
  passwordLabel: "Password",
  passwordHelp: (min, max) => `Use ${min} to ${max} characters.`,
  submit: "Post and create account",
  submitting: "Saving your project…",
  confirmationRequired: "Confirm your email before accessing your account or making the project visible to writers.",
  billingLater: "We’ll ask for your address and phone number when payment is due.",
  signIn: "Already have an account? Sign in",

  firstNameRequired: "Enter your first name.",
  lastNameRequired: "Enter your last name.",
  emailRequired: "Enter your email address.",
  emailInvalid: "Enter a valid email address.",
  passwordRequired: "Create a password.",
  passwordTooShort: (min) => `Use at least ${min} characters.`,
  passwordTooLong: (max) => `Use no more than ${max} characters.`,
  nameTooLong: (max) => `Use no more than ${max} characters.`,
  securityCheck: "Complete the security check to continue.",
  validationSummary: "Check the highlighted fields before posting.",
  tooManyAttempts: "Too many accounts have been created from this connection. Try again in about an hour.",
  pending: (key) => `[wording pending: registration.${key}]`,

  agreement: [
    "I have read and agree to the ", ["terms_of_service", "Terms of Service"], ", ", ["privacy_policy", "Privacy Policy"],
    ", ", ["plagiarism_policy", "Plagiarism Policy"], ", ", ["refund_policy", "Refund and Revision Policy"], ", ",
    ["video_chat", "Platform Video Call Policy"], ", ", ["cookie", "Cookie Policy"], " and ",
    ["ai_use", "Acceptable Uses of AI"], ". I also agree to the ", ["usage_agreement", "Platform Usage Agreement"],
    ", including its rules for conduct, responsibility for claims involving freelancers and ending platform access.",
  ],
  agreementHelp:
    "These cover using TUW, handling personal information and cookies, plagiarism, refunds and revisions, video calls and permitted uses of AI.",
  agreementRequired: "Read and accept the registration policies before posting.",

  existingHeading: "This email already has an account",
  existingBody: "Sign in to continue with this project. Your draft is saved.",
  existingButton: "Sign in and continue",
  existingUseAnother: "Use a different email",
  existingForgot: "Forgot your password?",

  failedHeading: "We couldn’t create your account",
  failedBody: "Your project hasn’t been posted. Check your details and try again.",
  failedRetry: "Try again",
}

// Key turn's confirmation lines, approved verbatim: guest-wording.md §4
// (confirmation.*). The link page (confirmation.linkPage.*) was adopted on
// advice 2026-10-24 under Marco's Desk card 406 ruling, and the rare refusals
// on advice 2026-10-24.
export const CONFIRMATION = {
  heading: "Confirm your email to post your project",
  sentTo: (email) => `We’ve sent a confirmation email to ${email}.`,
  saved: "Your project is saved and waiting for confirmation. Writers can’t see it yet.",
  code: {
    label: "Confirmation code",
    help: (length) => `Enter the ${length}-digit code from your confirmation email.`,
    digitLabel: (position, length) => `Digit ${position} of ${length}`,
    required: "Enter your confirmation code.",
    incomplete: (length) => `Enter all ${length} digits.`,
    invalid: "That code isn’t valid. Check your confirmation email and try again.",
    expired: "This code has expired. Request another confirmation email.",
  },
  linkHelp: "You can also use the confirmation button in that email, including on another device.",
  signInBeforeConfirm: "Your project is saved. Confirm your email before continuing to your account.",
  resend: "Resend confirmation email",
  resending: "Sending confirmation email…",
  expiry: (at) => `Confirm by ${at}. After that, we’ll delete this unconfirmed post.`,
  expiryUnchanged: "Resending the email or correcting your address won’t extend this deadline.",
  correctEmail: "Correct email address",
  resendSuccess: (email) => `We’ve sent another confirmation email to ${email}.`,
  deliveryFailed:
    "Your project is saved, but we couldn’t send the confirmation email. Check the address and try sending it again.",
  cooldown: (seconds) => `You can request another email in ${seconds} seconds.`,
  resendFailed: "We couldn’t send another confirmation email. Try again.",

  tooManyAttempts: "There have been too many confirmation attempts from your connection. Try again in about an hour.",
  notPending:
    "There’s no project here waiting for confirmation anymore. It may have been removed after the confirmation deadline.",
  accountConflict: "We can’t confirm a new account with this email address. Email support@dibbly.com for help.",
  postChanged:
    "Something about your project changed, so we couldn’t post it as saved. Your project is still saved, and you can email support@dibbly.com for help.",
  priceChanged:
    "We couldn’t post your project at the price you accepted, and nothing has been charged. Email support@dibbly.com and we’ll help.",
  postIncomplete: "Your saved project is missing something we need to post it. Email support@dibbly.com for help.",

  submit: "Confirm email and post project",
  submitting: "Confirming your email…",
  linkPage: {
    heading: "Confirm your email to post your project",
    body: "Enter the password you created when posting to confirm your email and post your project.",
    passwordLabel: "Password",
    passwordIncorrect: "That password isn’t correct. Try again.",
    tooManyAttempts:
      "You’ve entered the wrong password too many times. You can still confirm with the code on the device you posted from, or wait and try again later.",
  },
  link: {
    invalid: "This confirmation link isn’t valid. Return to confirmation to request another email.",
    expired: "This confirmation link has expired. Return to confirmation to request another email.",
  },
  return: "Return to confirmation",
  alreadyConfirmed: {
    heading: "Your email is already confirmed",
    body: "Continue to your project.",
    // new-flow-wording §27: on the guest page, which is never signed in.
    bodyNotSignedIn: "Your project is posted. Sign in here with the email and password you chose when posting.",
    button: "View my project",
  },
  wrongAccount: "You’re signed in to a different account. Sign out before continuing with this confirmation.",
  signOut: "Sign out",
  failed: "We couldn’t complete confirmation. Try again.",
  // §6 "Opened on another device" (reminder.return.noDraft.*), adopted on
  // advice 2026-10-24: the reminder's button where no posted draft opens.
  reminderNoDraft: {
    heading: "We can’t open your post here",
    body: "You may be using a different device or browser, or your unconfirmed post may have been deleted after the 30-day deadline. Open this reminder on the device and browser you used to post and enter the code from your confirmation email, or use the button in that email with the password you created when posting.",
  },
}

// Forwarding address's lines, approved verbatim: guest-wording.md §5
// (emailCorrection.*), the form opened from the waiting screen.
export const EMAIL_CORRECTION = {
  heading: "Correct your email address",
  current: (email) => `Current address: ${email}`,
  label: "Correct email address",
  help: "We’ll send a replacement confirmation email to this address.",
  preserved: "Your project details and accepted price stay the same.",
  oldLinks: "Codes and links sent to the previous address will stop working.",
  deadline: (at) => `Your confirmation deadline remains ${at}.`,
  submit: "Update email and send confirmation",
  submitting: "Updating your email…",
  cancel: "Back to confirmation",
  required: "Enter your email address.",
  invalid: "Enter a valid email address.",
  unchanged: "This is the address already on your post. You can resend the confirmation email from the previous screen.",
  inUse: {
    heading: "This email already has an account",
    body: "Sign in to that account to continue with your project.",
    signIn: "Sign in and continue",
    change: "Use a different email",
  },
  success: (email) => `Your email address is now ${email}. We’ve sent a replacement confirmation email there.`,
  failed: (email) => `We couldn’t update your email address. It’s still ${email}. Try again.`,
  deliveryFailed: (email) =>
    `Your email address is now ${email}, but we couldn’t send the confirmation email. Try sending it again.`,
  cooldown: (seconds) => `Please wait ${seconds} seconds before changing the address again.`,
  retry: "Try again",
  resend: "Send confirmation email",
}

// Sundown's lines, approved verbatim: guest-wording.md §7 (postExpired.*), for
// a pending post past its deadline while the server still has it. Once it is
// deleted, nothing recognises its cookie or links, and they read as invalid.
export const POST_EXPIRED = {
  heading: "This unconfirmed post has expired",
  body: (days) => `We didn’t receive email confirmation within ${days} days, so we deleted this post.`,
  recovery: "Start a new project to continue. You’ll need to enter the project details again and review current prices.",
  startNew: "Start a new project",
  existingAccount: "If you already have an established TUW account, you can still sign in. Its projects are unaffected.",
  signIn: "Sign in",
  oldConfirmation: "This code or link belongs to an expired post and can no longer confirm it.",
}

// The server's reasons for a post past its deadline (`expired`, the link's
// `link_expired`) or already deleted by Sundown (`post_expired`): each screen
// ends in POST_EXPIRED's lines.
export const POST_EXPIRED_REASONS = ["expired", "link_expired", "post_expired"]

// The confirmation refusals the waiting screen and the link page show the same
// way, by the server's reason. A post past its deadline is no longer pending.
export const CONFIRMATION_REFUSALS = {
  too_many_attempts: CONFIRMATION.tooManyAttempts,
  not_pending: CONFIRMATION.notPending,
  expired: POST_EXPIRED.oldConfirmation,
  post_expired: POST_EXPIRED.oldConfirmation,
  account_conflict: CONFIRMATION.accountConflict,
  post_changed: CONFIRMATION.postChanged,
  price_changed: CONFIRMATION.priceChanged,
  post_incomplete: CONFIRMATION.postIncomplete,
  confirmation_failed: CONFIRMATION.failed,
}
